import React from 'react'
import { theme } from '../../../core/design-system/theme'
import { usePaymentsViewModel } from '../hooks/usePaymentsViewModel'

const cardStyle = {
  background: theme.colors.card,
  border: '1px solid ' + theme.colors.border,
  borderRadius: 14,
  padding: 14,
  boxShadow: '0 2px 8px rgba(92,61,46,0.05)',
  marginBottom: 10,
}

function CreditCardIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      stroke={theme.colors.textSecondary}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  )
}

function MethodRow({ method }) {
  const [hovered, setHovered] = React.useState(false)
  return (
    <div
      style={{
        ...cardStyle,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        borderColor: hovered ? theme.colors.borderStrong : theme.colors.border,
        transition: 'border-color 0.15s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <CreditCardIcon />
      <span
        style={{
          flex: 1,
          fontFamily: theme.fonts.body,
          fontWeight: 600,
          fontSize: 14,
          color: theme.colors.textPrimary,
        }}
      >
        {method.brand} ••••{method.lastFour}
      </span>
      {method.isDefault && (
        <span
          style={{
            fontFamily: theme.fonts.body,
            fontSize: 11,
            fontWeight: 700,
            color: theme.colors.terracotta,
            background: theme.colors.peach,
            padding: '3px 8px',
            borderRadius: 99,
            flexShrink: 0,
          }}
        >
          Default
        </span>
      )}
    </div>
  )
}

function TransactionRow({ tx }) {
  const label = `Booking ${tx.bookingId.slice(0, 8).toUpperCase()}`
  const succeeded = tx.status === 'succeeded'
  const [hovered, setHovered] = React.useState(false)
  return (
    <div
      style={{
        ...cardStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        borderColor: hovered ? theme.colors.borderStrong : theme.colors.border,
        transition: 'border-color 0.15s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontFamily: theme.fonts.body,
            fontWeight: 600,
            fontSize: 14,
            color: theme.colors.textPrimary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </p>
        <span
          style={{
            display: 'inline-block',
            marginTop: 4,
            fontFamily: theme.fonts.body,
            fontSize: 11,
            fontWeight: 700,
            color: succeeded ? theme.colors.successText : theme.colors.textSecondary,
            background: succeeded ? theme.colors.successBg : theme.colors.border,
            padding: '2px 7px',
            borderRadius: 99,
            textTransform: 'capitalize',
          }}
        >
          {tx.status}
        </span>
      </div>
      <span
        style={{
          fontFamily: theme.fonts.body,
          fontWeight: 700,
          fontSize: 15,
          color: theme.colors.terracotta,
          flexShrink: 0,
        }}
      >
        ${Number(tx.amount).toFixed(2)}
      </span>
    </div>
  )
}

function SectionHeader({ children }) {
  return (
    <h2
      style={{
        fontFamily: theme.fonts.display,
        fontWeight: 600,
        fontSize: 17,
        color: theme.colors.textPrimary,
        margin: '24px 0 12px',
      }}
    >
      {children}
    </h2>
  )
}

function EmptyNote({ children }) {
  return (
    <p
      style={{
        fontFamily: theme.fonts.body,
        fontSize: 14,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        padding: '20px 0',
        margin: 0,
      }}
    >
      {children}
    </p>
  )
}

function SkeletonCard() {
  return (
    <div
      style={{
        height: 56,
        background: theme.colors.bgSecondary,
        borderRadius: 14,
        marginBottom: 10,
        opacity: 0.7,
      }}
    />
  )
}

export default function PaymentsView({ repository, session }) {
  const { status, methods, transactions, error } = usePaymentsViewModel({ repository, session })

  return (
    <main
      className="page-enter"
      style={{
        background: theme.colors.cream,
        minHeight: '100vh',
        paddingBottom: 90,
        paddingTop: 24,
      }}
    >
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '0 20px' }}>
        <h1
          style={{
            fontFamily: theme.fonts.display,
            fontWeight: 600,
            fontSize: 22,
            color: theme.colors.textPrimary,
            margin: '0 0 4px',
          }}
        >
          Payments
        </h1>

        {status === 'failed' && (
          <p
            role="alert"
            style={{
              fontFamily: theme.fonts.body,
              color: theme.colors.errorText,
              padding: 16,
              background: theme.colors.errorBg,
              borderRadius: 12,
              marginTop: 16,
              marginBottom: 0,
            }}
          >
            {error}
          </p>
        )}

        <SectionHeader>Payment Methods</SectionHeader>
        {status === 'loading' && <SkeletonCard />}
        {status === 'loaded' && methods.length === 0 && (
          <EmptyNote>No payment methods saved yet.</EmptyNote>
        )}
        {status === 'loaded' && methods.map((m) => <MethodRow key={m.id} method={m} />)}

        <SectionHeader>Transaction History</SectionHeader>
        {status === 'loading' && <SkeletonCard />}
        {status === 'loaded' && transactions.length === 0 && (
          <EmptyNote>No transactions yet.</EmptyNote>
        )}
        {status === 'loaded' && transactions.map((tx) => <TransactionRow key={tx.id} tx={tx} />)}
      </div>
    </main>
  )
}
